import { Component, Input, OnInit, ElementRef, ViewChild, OnDestroy } from "@angular/core";
import { GameService } from "../../../../shared/services/game/game.service";
import {
  GamePoints,
  PrizeDetail,
} from "src/app/shared/constants/divisional.interface";
import { GAMETYPES, PRIZETYPES } from "src/app/shared/constants/constants";
import { properties } from "src/environments/properties";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: "app-prize-grid",
  templateUrl: "./prize-grid.component.html",
  styleUrls: ["./prize-grid.component.scss"],
})
export class PrizeGridComponent implements OnInit, OnDestroy {
  @Input()
  public gameId: string = "";
  @Input()
  public userInfo: any;
  @ViewChild('prizesContainer') prizesContainerEle: ElementRef;

  public displayCount: number = 0;
  public prizesVisible: number = 6;
  public prizeStep: number = 6;
  public showGrid: boolean = false;
  public loading: boolean = false;

  public prizes = [];
  public serviceErrorMessage: string = null;
  public numberOfPrizes: number = 0;
  public prizePoints = [];
  public gamePoints: GamePoints = {
    totalGamePoints: 0,
    totalPrizesWon: 0,
  };
  public userOptedIn: boolean = false;
  public programName: string = "";
  public gameType: string = "";
  public GAMETYPES = GAMETYPES;
  public epHeadlineText: string = properties.availablePrizes;
  public epSubHeadlineText: string = properties.playProgramName;
  public tokenLabelSingular: string = "";
  public tokenLabelPlural: string = "";
  private destroy$ = new Subject<void>();

  constructor(public gameService: GameService) {
    this.prizeStep = this.prizesVisible;
    this.prizes = new Array(this.prizesVisible);
  }

  ngOnInit(): void {
    this.gameService.gamePointsUpdatedObservable.subscribe((gamePoints) => {
      this.gamePoints = gamePoints;
    });
    this.gameService.refreshGameDetailsObservable.pipe(takeUntil(this.destroy$)).subscribe((details) => {
      // Game is loaded
      if (details.codes == this.gameId) {
        this.loading = true;
        this.programName = details.names;
        this.userOptedIn = details.userOptedIn;
        this.gameType = details.type;
        this.tokenLabelSingular = details.tokenLabelSingular;
        this.tokenLabelPlural = details.tokenLabelPlural;
        this.updateSubHeadlineText(this.programName);
        this.updatePrizes();
      }
    });

    this.gameService.refreshOptInStatusOnlyObservable.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if(data) {
        this.userOptedIn = true;
      }
    });

    this.gameService.prizeRedeemedObservable.subscribe((flag) => {
      this.updatePrizes();
    });
  }

  loadTillPrizeMatch(prizes, selectedPrize) {
      if(selectedPrize && prizes.length) {
        let tempPrizeStep = 0;
        let foundPrize = false;
        for(let key in prizes){
          for (let i = 0; i < prizes[key].length; i++) {
            tempPrizeStep = tempPrizeStep + 1;
            if(prizes[key][i].prizeId === selectedPrize) {
              foundPrize = true;
              break;
            }
          }
          if(foundPrize) 
            break;
        }
        if(!foundPrize) {
          this.prizesVisible = this.prizeStep;
        } else {
          this.prizesVisible = ( foundPrize && tempPrizeStep <= 6 )  ? this.prizeStep : tempPrizeStep;
        }
    }
  }

  updatePrizes() {
    this.gameService.getPrizes(this.gameId, this.userInfo).subscribe(
      (response) => {
        if (response && response.prizes) {
          let prizes = response.prizes;

          /*

                Still more to do here

                */

          if (response.isFreshPassSubscribedUser) {
            prizes = prizes.filter(
              (prize) => prize.type != PRIZETYPES.FRESH_PASS
            );
          } else {
            prizes = prizes.filter(
              (prize) => prize.type != PRIZETYPES.ALTERNATE_PRIZES
            );
          }

          if (this.gameType === GAMETYPES.DIVISION) {
            this.numberOfPrizes = prizes.length;
            this.prizes = this.groupBy(prizes, "burnPoints");
            this.prizePoints = Object.keys(this.prizes);
           
            // prizes to be displayed in the grid till it matches localstorage prizeGameId
            let selectedPrize = localStorage.getItem("selectedPrize");
            if(selectedPrize && this.prizes.length) {
              this.loadTillPrizeMatch(this.prizes, selectedPrize);
              localStorage.removeItem("selectedPrize");
              // Wait for the Prize cards to render and focus on the last prize card details link to navigate to back to the selected prize details
                setTimeout(() => {
                  const elements = this.prizesContainerEle.nativeElement.querySelectorAll('.prize-card.card');
                  if(elements.length) {
                    elements.forEach((element: HTMLElement) => {
                      if (element.id === "prize-card"+selectedPrize) {
                        const detailsLink = element.querySelector('a.prize-card-reward-details') as HTMLAnchorElement;
                        detailsLink.focus();
                        element.scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"});
                        
                      }
                    });
                  }
                });
              
            }
          } else if (this.gameType === GAMETYPES.EPISODIC) {
            this.prizes = prizes;
          }
        } else {
          this.prizes = [];
        }

        if (response && response.serviceErrors && response.serviceErrors[0]) {
          this.serviceErrorMessage = response.serviceErrors[0].message;
        }

        this.loading = false;
        this.showGrid = true;
      },
      (error) => {
        this.prizes = [];
        this.loading = false;
        this.showGrid = true;
      }
    );
  }

  updateSubHeadlineText(programName: string) {
    this.epSubHeadlineText =
      this.epSubHeadlineText &&
      this.epSubHeadlineText.replace(/{programName}/g, programName);
  }

  groupBy(prizes: PrizeDetail[], key: string) {
    if (prizes && prizes.length) {
      let groupedPrizes = prizes.reduce((result, currentValue) => {
        result[currentValue[key]] = result[currentValue[key]] || [];
        result[currentValue[key]].push(currentValue);
        return result;
      }, []);
      for (let item in groupedPrizes) {
        groupedPrizes[item] =
          groupedPrizes[item].length > 1
            ? groupedPrizes[item].sort(this.sortByRanking)
            : groupedPrizes[item];
      }
      return groupedPrizes;
    } else {
      return [];
    }
  }

  sortByRanking(a: PrizeDetail, b: PrizeDetail) {
    if (a["ranking"] < b["ranking"]) {
      return -1;
    }
    if (a["ranking"] > b["ranking"]) {
      return 1;
    }
    return 0;
  }

  public showMorePrizes() {
    this.prizesVisible += this.prizeStep;
    let prizeCards: any = document.getElementsByClassName(
      "prize-card-reward-details"
    );
    let activeCardIndex = this.prizesVisible - this.prizeStep;
    setTimeout(() => {
      prizeCards &&
        prizeCards[activeCardIndex] &&
        prizeCards[activeCardIndex].focus();
    }, 100);
    if (window["AB"] && window["AB"].DATALAYER) {
      window["AB"].DATALAYER.setLinkClickEvents(
        `games|${this.gameId}:game-catalog`
      );
    }
  }

  public resetCount() {
    this.displayCount = 0;
    return true;
  }

  public showItem() {
    this.displayCount++;
    return this.displayCount <= this.prizesVisible;
  }

  public showContainer() {
    return this.displayCount < this.prizesVisible;
  }

  public groupingLabel(points: number) {
    if (points == 1) {
      return points + " " + this.tokenLabelSingular;
    } else {
      return points + " " + this.tokenLabelPlural;
    }
  }
  ngOnDestroy() {
    this.destroy$.next();  // This will trigger the unsubscription
    this.destroy$.complete();
  }
}
